import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { LoggedInTeaserElement, ManageOrdersElement } from '@shared/dream-components';

import RequestAccessAction from '~/components/RequestAccessAction';
import { useOrdersContext } from '~/context/SubscriptionManagement';
import { downloadFulfillmentIcs } from '~/utils/downloadFulfillmentIcs';

// Owns the @calcom/atoms import chain; lazy so Cal.com only downloads when a
// reschedule modal actually opens.
const CoachingBookingModal = lazy(() => import('~/components/DreamBuilder/nodes/Button/CoachingBookingModal'));

/**
 * Renders the Orders tab — the request-access teaser (read-only subscribers), the orders list, and
 * the Cal.com coaching-reschedule modal. Orders, handlers, receipt state, and reschedule state all
 * come from `OrdersProvider` via context. Pure UI, no props, no data logic.
 */
export const OrdersSection = () => {
  const { t } = useTranslation();
  const {
    orders,
    canManageOrders,
    isLoggedIn,
    pendingReceiptOrderId,
    subscriberEmail,
    subscriberFullName,
    publicationId,
    onViewReceipt,
    onClickReviewProduct,
    onAccessFulfillment,
    isReschedulingModalOpen,
    isRescheduling,
    reschedulingData,
    onCloseRescheduling,
    onRescheduleBookingSuccess,
  } = useOrdersContext();

  return (
    <>
      {isReschedulingModalOpen && reschedulingData && (
        <Suspense fallback={null}>
          <CoachingBookingModal
            shouldDisableConfirmButton={isRescheduling}
            fulfillmentMetadata={reschedulingData?.fulfillmentMetadata}
            eventSlug={reschedulingData?.calComEventTypeSlug}
            calComUserName={reschedulingData?.calComUserName}
            onClose={onCloseRescheduling}
            onBookingSuccess={onRescheduleBookingSuccess(reschedulingData.fulfillmentId)}
            isOpen={isReschedulingModalOpen}
            currentSubscriberEmail={subscriberEmail}
            currentSubscriberName={subscriberFullName}
          />
        </Suspense>
      )}

      {isLoggedIn && !canManageOrders && (
        <RequestAccessAction email={subscriberEmail} publicationId={publicationId} redirectTo={window.location.href}>
          {({ isLoading }) => {
            return (
              <LoggedInTeaserElement
                element={{
                  type: 'loggedInTeaser',
                }}
                isButtonDisabled={isLoading}
                buttonLabel={t('manage.orders.authorize_cta')}
                buttonType="submit"
                translations={{
                  'manage.orders.title': t('manage.orders.title'),
                  'manage.orders.description': t('manage.orders.description'),
                }}
              >
                {null}
              </LoggedInTeaserElement>
            );
          }}
        </RequestAccessAction>
      )}

      {/*
        Orders
        - Only shown to logged in subscribers
      */}
      {canManageOrders && Array.isArray(orders) && orders.length > 0 && (
        <ManageOrdersElement
          element={{
            type: 'manageOrders',
            attrs: {
              data: {
                orders,
              },
            },
          }}
          isLive
          handleViewReceipt={onViewReceipt}
          handleClickReviewProduct={onClickReviewProduct}
          pendingReceiptOrderId={pendingReceiptOrderId}
          translations={{
            'manage.orders.title': t('manage.orders.title'),
            'manage.orders.description': t('manage.orders.description'),
            'manage.orders.order.number': t('manage.orders.order.number'),
            'manage.orders.order.creation_date': t('manage.orders.order.creation_date'),
            'manage.orders.order.total_amount': t('manage.orders.order.total_amount'),
            'manage.orders.order.view_receipt_cta': t('manage.orders.order.view_receipt_cta'),
            'manage.orders.delivered_on': t('manage.orders.delivered_on'),
            'manage.orders.order.cta.file': t('manage.orders.order.cta.file'),
            'manage.orders.order.cta.link': t('manage.orders.order.cta.link'),
            'manage.orders.order.cta.coaching_session': t('manage.orders.order.cta.coaching_session'),
            'manage.orders.order.cta.webinar': t('manage.orders.order.cta.webinar'),
            'manage.orders.order.cta.add_to_calendar': t('manage.orders.order.cta.add_to_calendar'),
            'manage.orders.order.cta.create_product_review': t('manage.orders.order.cta.create_product_review'),
            'manage.orders.order.cta.edit_product_review': t('manage.orders.order.cta.edit_product_review'),
            'manage.orders.order.cta.accessing': t('manage.orders.order.cta.accessing'),
            'manage.orders.order.cta.cancelled': t('manage.orders.order.cta.cancelled'),
            'manage.orders.order.booking.meeting_details': t('manage.orders.order.booking.meeting_details'),
            'manage.orders.order.booking.video_call_link': t('manage.orders.order.booking.video_call_link'),
            'manage.orders.order.webinar.event_details': t('manage.orders.order.webinar.event_details'),
            'manage.orders.order.webinar.cancelled': t('manage.orders.order.webinar.cancelled'),
            'manage.orders.order.review.title': t('manage.orders.order.review.title'),
          }}
          onAccessFulfillment={onAccessFulfillment}
          onAddToCalendar={downloadFulfillmentIcs}
        >
          {null}
        </ManageOrdersElement>
      )}
    </>
  );
};
